// ============================================
// FILE: frontend/src/services/stripeService.js
// VERSION CORRIGÉE
// ============================================

import { get, post } from '../utils/apiClient'
import { loadStripe } from '@stripe/stripe-js'

// ============================================
// STRIPE PUBLISHABLE KEY DYNAMIQUE
// ============================================
// Cache module-level : la clé est récupérée une seule fois depuis le backend
// puis loadStripe() est appelé une seule fois par clé.

const _stripePromiseCache = {}
let _configPromise = null

/**
 * Récupère la publishable key depuis le backend et retourne une stripePromise.
 * Le backend résout la clé selon le stripe_mode de l'organisation (test/live).
 *
 * Cache module-level :
 * - 1 seul fetch /api/stripe/config par session
 * - 1 seul loadStripe() par clé
 *
 * @returns {Promise<Stripe|null>} stripePromise prête pour <Elements>
 */
export async function getStripeConfig() {
  if (!_configPromise) {
    _configPromise = get('/stripe/config').catch((err) => {
      console.error('❌ Impossible de charger la config Stripe:', err)
      _configPromise = null // reset pour retry
      return null
    })
  }
  return _configPromise
}

/**
 * Reset le cache Stripe. À appeler lors d'un switch de tenant
 * pour forcer le rechargement de la bonne publishable key.
 */
export function resetStripeCache() {
  _configPromise = null
  // Note : on ne vide pas _stripePromiseCache car loadStripe()
  // par clé reste valide — seule la config (quel tenant = quelle clé) est invalidée.
}

export function getStripePromise() {
  return getStripeConfig().then((config) => {
    if (!config?.publishableKey) {
      // Fallback dev uniquement
      const fallback = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
      if (fallback) {
        console.warn('⚠️ Fallback sur VITE_STRIPE_PUBLISHABLE_KEY (dev)')
        if (!_stripePromiseCache[fallback]) {
          _stripePromiseCache[fallback] = loadStripe(fallback)
        }
        return _stripePromiseCache[fallback]
      }
      return null
    }

    const key = config.publishableKey
    if (!_stripePromiseCache[key]) {
      _stripePromiseCache[key] = loadStripe(key)
    }
    return _stripePromiseCache[key]
  })
}

export const stripeService = {
  // ============================================
  // ABONNEMENTS
  // ============================================

  // Récupérer les plans disponibles depuis Stripe
  async getAvailablePlans() {
    return await get('/stripe/plans')
  },

  // Créer une session Checkout Stripe
  async createCheckoutSession(plan) {
    return await post('/stripe/subscriptions/checkout', { plan })
  },

  // Récupérer l'abonnement actuel
  async getCurrentSubscription() {
    return await get('/stripe/subscriptions/current')
  },

  // Annuler un abonnement
  async cancelSubscription(immediately = false) {
    return await post('/stripe/subscriptions/cancel', { immediately })
  },

  // Réactiver un abonnement annulé
  async reactivateSubscription() {
    return await post('/stripe/subscriptions/reactivate', {})
  },

  // Créer un portail de facturation
  async createBillingPortal() {
    return await post('/stripe/subscriptions/portal', {})
  },

  // Récupérer les factures Stripe
  async getInvoices() {
    return await get('/stripe/invoices')
  },

  // ============================================
  // STRIPE CONNECT
  // ============================================

  // Créer un compte Connect
  async createConnectAccount(email) {
    return await post('/stripe/connect/account', { email })
  },

  // Obtenir le lien d'onboarding Connect
  async getConnectOnboardingLink() {
    return await post('/stripe/connect/onboarding-link', {})
  },

  // Obtenir le lien du dashboard Connect
  async getConnectDashboardLink() {
    return await post('/stripe/connect/dashboard-link', {})
  },

  // Récupérer le statut du compte Connect
  async getConnectStatus() {
    return await get('/stripe/connect/status')
  },
}
